import { useState } from "react";
import { Copy, Check } from "lucide-react";

/**
 * CopyPairedUcasButton — small icon button that copies the paired
 * UCAS code (e.g. "L0V0 · O33") to the clipboard so families can
 * paste it straight into UCAS Track / Apply. Renders a short green
 * "Copied" state for ~1.5s after a successful copy.
 *
 * The copied string uses a space separator (not the middle-dot),
 * because UCAS Search's own query field treats "L0V0 O33" as two
 * tokens and immediately returns the right course card.
 */
export default function CopyPairedUcasButton({ courseCode, institutionCode }) {
  const [copied, setCopied] = useState(false);
  if (!courseCode) return null;
  const payload = institutionCode
    ? `${courseCode} ${institutionCode}`
    : courseCode;

  const onClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* Clipboard blocked (older browser or sandboxed iframe) — the
         visible UCAS link stays clickable, so failure is silent. */
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="degree-ucas-copy-btn"
      title={copied ? "Copied to clipboard" : `Copy "${payload}"`}
      aria-label={`Copy paired UCAS code ${payload}`}
      className={`inline-flex items-center justify-center h-6 w-6 rounded border text-stone-500 hover:text-[#4C1D95] hover:border-[#4C1D95] transition ml-1 align-middle ${
        copied ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-stone-300"
      }`}
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}
